import React from 'react';
import { Check, Plus, UserRound } from 'lucide-react';
import { avatarColorClass, initialsFromUser } from '../../../components/UserMultiSelect';
import { PRIORITY_META, STATUS_META, memberName, type TaskStatus, type TaskTone } from '../taskViews';
import type { TaskMember, TaskPriority } from '../types';

/** Abschnittsüberschrift („Ansichten", „Beschreibung") — ohne Caps, wie FILTER_GROUP_LABEL_CLASS. */
export const TASK_SECTION_LABEL_CLASS = 'text-xs font-medium text-app-text-subtle';

/** Kompakte Dropdown-Trigger in Kopfzeilen (30px wie im Entwurf). */
export const TASK_COMPACT_TRIGGER_CLASS = '!min-h-0 h-[30px] !py-0 text-xs';

export const TONE_TEXT_CLASS: Record<TaskTone, string> = {
  overdue: 'text-red-600',
  today: 'text-green-600',
  default: 'text-app-text-subtle',
};

const PRIORITY_PILL_CLASS: Record<TaskPriority, string> = {
  hoch: 'bg-red-50 text-red-700',
  mittel: 'bg-amber-50 text-amber-800',
  niedrig: 'bg-app-bg text-app-text-subtle',
};

const STATUS_PILL_CLASS: Record<TaskStatus, string> = {
  overdue: 'bg-red-50 text-red-700',
  open: 'bg-green-50 text-green-700',
  done: 'bg-app-bg text-app-text-subtle',
};

export function PriorityPill({ priority }: { priority: TaskPriority }) {
  return (
    <span
      title="Priorität"
      className={`inline-flex h-5 items-center whitespace-nowrap rounded px-1.5 text-[11px] font-medium ${PRIORITY_PILL_CLASS[priority]}`}
    >
      {PRIORITY_META[priority].label}
    </span>
  );
}

export function StatusPill({ status }: { status: TaskStatus }) {
  return (
    <span
      className={`inline-flex h-5 items-center whitespace-nowrap rounded px-2 text-xs font-medium ${STATUS_PILL_CLASS[status]}`}
    >
      {STATUS_META[status].label}
    </span>
  );
}

const AVATAR_SIZE_CLASS = {
  sm: 'h-[22px] w-[22px] text-[9px]',
  md: 'h-6 w-6 text-[10px]',
  lg: 'h-7 w-7 text-[11px]',
} as const;

export function TaskAvatar({ member, size = 'md' }: { member: TaskMember | null | undefined; size?: keyof typeof AVATAR_SIZE_CLASS }) {
  const sizeClass = AVATAR_SIZE_CLASS[size];
  if (!member) {
    return (
      <span
        title="Nicht zugewiesen"
        className={`inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-app-border-strong text-app-text-subtle ${sizeClass}`}
      >
        <UserRound className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span
      title={memberName(member)}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white ${avatarColorClass(member.id)} ${sizeClass}`}
    >
      {initialsFromUser(member)}
    </span>
  );
}

/** Runder Erledigt-Haken; offener Rand rot bei Priorität „Hoch". */
export function TaskCheckButton({
  done,
  priority,
  onToggle,
  disabled,
}: {
  done: boolean;
  priority: TaskPriority;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const idle = priority === 'hoch' ? 'border-red-400' : 'border-app-border-strong';
  return (
    <button
      type="button"
      disabled={disabled}
      title={done ? 'Als offen markieren' : 'Als erledigt markieren'}
      aria-label={done ? 'Als offen markieren' : 'Als erledigt markieren'}
      aria-pressed={done}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      // Touch: unsichtbar vergrößerte Trefferfläche (~40px), der Kreis bleibt klein.
      className={`relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors disabled:cursor-default pointer-coarse:before:absolute pointer-coarse:before:-inset-[11px] pointer-coarse:before:content-[''] ${
        done
          ? 'border-green-500 bg-green-500 text-white'
          : `${idle} bg-app-surface text-transparent enabled:hover:border-green-500 enabled:hover:text-green-500`
      }`}
    >
      <Check className="h-[11px] w-[11px]" strokeWidth={3} />
    </button>
  );
}

export type SegmentOption<T extends string> = { value: T; label: string };

/** Segmented Control im Stil von CalendarViewSwitcher, mit Textlabels. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
  ariaLabel,
  fullWidth = false,
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** Füllt die Zeile (gleich breit/hoch wie Formular-Dropdowns, h-8). */
  fullWidth?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`flex items-center gap-0.5 rounded-md bg-app-bg p-0.5 ${fullWidth ? 'w-full' : ''}`}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={`${fullWidth ? 'h-7 flex-1' : 'h-[26px]'} whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors disabled:cursor-default ${
              active
                ? 'bg-app-surface text-pipedrive-blue shadow-sm'
                : 'text-app-text-subtle enabled:hover:text-app-text'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Mobil: runder „+"-Knopf rechts oben für ein neues To-do. */
export function MobileCreateButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Neues To-do"
      onClick={onClick}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pipedrive-blue text-white shadow-sm md:hidden"
    >
      <Plus className="h-5 w-5" strokeWidth={2} />
    </button>
  );
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-app-border bg-app-surface px-1.5 py-px font-mono text-[11px] text-app-text-muted">
      {children}
    </kbd>
  );
}

export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(el.closest('[contenteditable="true"]'));
}
